import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.categorySlug === movie.categorySlug)
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-950">
          <Navbar />
          <main className="pt-20">
            <div className="w-full px-4 md:px-6 py-20">
              <div className="max-w-7xl mx-auto text-center">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-600"></i>
                </div>
                <h1 className="text-2xl font-bold text-white mb-3">影片未找到</h1>
                <p className="text-foreground-500 mb-6">抱歉，您要查看的影片不存在或已下架。</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-all"
                >
                  <i className="ri-home-line"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const pageTitle = `${movie.title} - 天美麻花星空影视免费观看高清不缓冲`;
  const pageDesc = `${movie.title}在线观看，天美麻花星空影视提供高清流畅播放。${movie.description.substring(0, 100)}`;

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '影视分类', to: '/category' },
    { label: movie.category, to: `/category/${movie.categorySlug}` },
    { label: movie.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: movie.title,
    description: movie.description,
    image: movie.coverImage,
    datePublished: movie.updateDate,
    author: { '@type': 'Organization', name: '天美麻花星空影视' },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article>
                <div className="flex flex-col md:flex-row gap-8 mb-10">
                  <div className="flex-shrink-0">
                    <img
                      src={movie.coverImage}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} - 天美麻花星空影视免费观看`}
                      className="w-full md:w-[300px] h-[420px] object-cover rounded-xl"
                    />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{movie.title}</h1>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="flex items-center gap-1 text-primary-400 text-sm font-semibold">
                        <i className="ri-star-fill"></i>
                        {movie.rating}
                      </span>
                      <span className="text-foreground-600">|</span>
                      <span className="text-foreground-400 text-sm">{movie.year}</span>
                      <span className="text-foreground-600">|</span>
                      <span className="text-foreground-400 text-sm">{movie.duration}</span>
                      <span className="text-foreground-600">|</span>
                      <span className="text-foreground-400 text-sm">{movie.region}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {movie.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-6 text-sm">
                      <div>
                        <span className="text-foreground-600">导演：</span>
                        <span className="text-foreground-300">{movie.director}</span>
                      </div>
                      <div>
                        <span className="text-foreground-600">主演：</span>
                        <span className="text-foreground-300">{movie.actors.join(' / ')}</span>
                      </div>
                      <div>
                        <span className="text-foreground-600">地区：</span>
                        <span className="text-foreground-300">{movie.region}</span>
                      </div>
                      <div>
                        <span className="text-foreground-600">更新：</span>
                        <span className="text-foreground-300">{movie.updateDate}</span>
                      </div>
                      <div>
                        <span className="text-foreground-600">播放量：</span>
                        <span className="text-foreground-300">
                          {movie.views > 10000 ? `${(movie.views / 10000).toFixed(0)}万次` : `${movie.views}次`}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button className="flex-1 px-6 py-3 rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-all duration-300 flex items-center justify-center gap-2">
                        <i className="ri-play-circle-fill text-lg"></i>
                        立即播放
                      </button>
                      <button className="flex-1 px-6 py-3 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2">
                        <i className="ri-heart-line"></i>
                        收藏影片
                      </button>
                    </div>
                  </div>
                </div>

                <section className="mb-10">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                    剧情简介
                  </h2>
                  <p className="text-foreground-400 leading-relaxed text-sm">{movie.fullDescription}</p>
                </section>

                <section className="mb-10">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    影人信息
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <h3 className="text-foreground-600 text-xs mb-1">导演</h3>
                      <p className="text-white text-sm font-medium">{movie.director}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <h3 className="text-foreground-600 text-xs mb-1">主演</h3>
                      <p className="text-white text-sm font-medium">{movie.actors.join(' / ')}</p>
                    </div>
                  </div>
                </section>
              </article>

              {relatedMovies.length > 0 && (
                <section className="pb-12">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <span className="w-1 h-5 rounded-full bg-secondary-500"></span>
                      相关推荐
                    </h2>
                    <Link
                      to={`/category/${movie.categorySlug}`}
                      className="text-foreground-500 hover:text-primary-400 text-sm transition-colors flex items-center gap-1"
                    >
                      更多{movie.category} <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {relatedMovies.map((m) => (
                      <div key={m.id} className="flex justify-center">
                        <MovieCard movie={m} size="sm" />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}